import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { core } from "../../lib/api";
import type { Chat, SendMessageResult } from "./types";

/** Matches the cadence agreed for the CRM: one poll a minute, foreground only. */
export const CHAT_POLL_INTERVAL_MS = 60_000;
/** An open conversation polls faster -- a minute is a long time to wait to see
 * an inbound reply while the thread is actually on screen. */
export const OPEN_CHAT_POLL_INTERVAL_MS = 5_000;

export function useActiveChats(enabled = true) {
  return useQuery({
    queryKey: ["whatsapp-chats"],
    queryFn: () =>
      core<{ chats: Chat[] }>("/api/v1/crm/whatsapp/chats").then((r) => r.chats || []),
    refetchInterval: CHAT_POLL_INTERVAL_MS,
    refetchIntervalInBackground: false,
    enabled,
  });
}

export function useChatByPhone(phone: string | undefined) {
  return useQuery({
    queryKey: ["whatsapp-chat", phone],
    queryFn: () =>
      core<Chat | null>(
        `/api/v1/crm/whatsapp/chats/by-phone?phone=${encodeURIComponent(phone || "")}`
      ),
    refetchInterval: OPEN_CHAT_POLL_INTERVAL_MS,
    refetchIntervalInBackground: false,
    enabled: Boolean(phone),
  });
}

export function useSendMessage(phone: string | undefined) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (body: string) =>
      core<SendMessageResult>(
        `/api/v1/crm/whatsapp/chats/by-phone/messages?phone=${encodeURIComponent(phone || "")}`,
        { method: "POST", body: JSON.stringify({ body }) }
      ),

    // The notifications service writes the durable row as it sends, so at a
    // 60s poll the agent would otherwise wait up to a minute to see their own
    // message. Show it immediately and let the next poll reconcile.
    onSuccess: (result) => {
      queryClient.setQueryData<Chat | null>(["whatsapp-chat", phone], (current) => {
        if (!current) return current;
        return {
          ...current,
          last_message_at: result.at,
          last_direction: "out",
          messages: [
            ...current.messages,
            {
              direction: "out",
              body: result.body,
              message_type: "text",
              status: result.status,
              at: result.at,
              provider_message_id: null,
              event_id: result.event_id,
            },
          ],
        };
      });
      queryClient.invalidateQueries({ queryKey: ["whatsapp-chats"] });
    },
  });
}
